import React, {
  useState,
} from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import {
  useQueryClient,
} from '@tanstack/react-query';
import {
  ProviderEntryConfigDTO,
  ProviderEntryDTO,
} from 'dtos';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import ToolTip from 'core/components/Tooltip';
import clsx from 'clsx';
import ProviderCard from 'pages/Dashboard/components/ReferringProvider/ProviderCard';
import ProviderContactFields from 'pages/Dashboard/components/ReferringProvider/ProviderContactFields';
import {
  getEmptyProviderConfig,
  normalizeProviderConfigPayload,
} from 'pages/Dashboard/components/ReferringProvider/providerEntryConfig';
import {
  getProviderEntryConfigQueryKey,
  getProviderEntryConfigTarget,
} from 'pages/Dashboard/components/ReferringProvider/providerEntryConfigTarget';
import {
  useUpsertProviderEntryConfig,
} from 'pages/Dashboard/components/ReferringProvider/hooks';

export type ReferringProviderContactDialogParams = {
  provider: ProviderEntryDTO;
  providerConfig: ProviderEntryConfigDTO | null;
  onSaveSuccess: (savedConfig: ProviderEntryConfigDTO) => void;
};

type Props = ReferringProviderContactDialogParams & {
  onClose: () => void;
};

export default function ReferringProviderContactDialog({
  provider,
  providerConfig,
  onClose,
  onSaveSuccess,
}: Props) {
  const queryClient = useQueryClient();
  const { mutateAsync: saveProviderConfig } = useUpsertProviderEntryConfig();
  const target = getProviderEntryConfigTarget(provider);
  const [saveError, setSaveError] = useState<string | null>(null);
  const formProps = useForm<ProviderEntryConfigDTO>({
    mode: 'all',
    defaultValues: {
      ...getEmptyProviderConfig(provider.providerEntryId),
      ...providerConfig,
      faxNumber: '',
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = formProps;
  const isSaveDisabled = isSubmitting || !isValid;
  const saveDisabledReason = isSubmitting
    ? 'Saving provider contact'
    : 'Enter a valid fax number to continue';

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const onSubmit = async (formData: ProviderEntryConfigDTO) => {
    setSaveError(null);
    try {
      const savedConfig = await saveProviderConfig(normalizeProviderConfigPayload(formData, target));
      await queryClient.invalidateQueries({ queryKey: getProviderEntryConfigQueryKey(target) });
      onClose();
      onSaveSuccess(savedConfig);
    } catch {
      setSaveError('Could not save the provider contact. Please try again.');
    }
  };

  return (
    <Dialog
      open
      title="Send to Referring Provider"
      onClose={handleClose}
      className="!w-96"
    >
      <FormProvider {...formProps}>
        <div className="populate-form flex flex-col gap-form-row">
          <ProviderCard provider={provider} />
          <ProviderContactFields
            fields={['fax']}
            requiredFields={['fax']}
            disabled={isSubmitting}
            idPrefix="referring-provider"
          />
          {saveError && <p role="alert" className="text-sm text-red-600">{saveError}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <ToolTip
              title={saveDisabledReason}
              hide={!isSaveDisabled}
            >
              <span
                className={clsx('inline-flex rounded', {
                  'cursor-not-allowed hover:ring-1 hover:ring-gray-300': isSaveDisabled,
                })}
              >
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSaveDisabled}
                  onClick={handleSubmit(onSubmit)}
                >
                  Save & Continue
                </Button>
              </span>
            </ToolTip>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
